/**
 * Meta description for a profile page.
 *
 * Ported from sifa-web so the description and the JSON-LD `jobTitle` are
 * derived from one place. They previously used different rules for "which
 * position is current" and could name different roles on the same page.
 */

import { pickPrimaryPosition } from '../logic/primary-position.js';
import { isRoleLineRedundant } from '../profile/headline-role-dedupe.js';
import { filterHidden } from '../profile/section-model.js';
import type { LocationValue } from '../types/index.js';
import { formatLocation } from './location-utils.js';

/**
 * Treat a headline as meaningful only when it contains at least one letter or
 * digit. Profiles carry headlines that are a single emoji, which reads as
 * garbage in a search result snippet.
 */
const MEANINGFUL_TEXT_RE = /[\p{L}\p{N}]/u;

export interface MetaDescriptionPosition {
  readonly company?: string;
  /**
   * Current canonical name of the linked company entity, resolved by the
   * AppView at read time. Preferred over the free-text `company` snapshot so a
   * corrected company name (and a decoded one) shows here, not the value frozen
   * into the position record.
   */
  readonly entityName?: string;
  readonly title?: string;
  readonly startedAt?: string;
  readonly endedAt?: string;
  readonly primary?: boolean;
  readonly hidden?: boolean;
}

export interface MetaDescriptionInput {
  readonly handle: string;
  readonly displayName?: string;
  readonly headline?: string;
  readonly location?: LocationValue | null;
  readonly positions?: readonly MetaDescriptionPosition[];
}

export function buildMetaDescription(profile: MetaDescriptionInput): string {
  const parts: string[] = [];

  if (typeof profile.headline === 'string' && MEANINGFUL_TEXT_RE.test(profile.headline)) {
    parts.push(profile.headline);
  }

  const positions = filterHidden(profile.positions ? [...profile.positions] : undefined);
  const currentPosition = pickPrimaryPosition(positions);
  if (currentPosition) {
    const positionParts: string[] = [];
    if (currentPosition.title) positionParts.push(currentPosition.title);
    const company = currentPosition.entityName ?? currentPosition.company;
    if (company) positionParts.push(`at ${company}`);
    if (positionParts.length > 0) {
      const roleLine = positionParts.join(' ');
      // Skip the derived role line when the headline already says the same thing
      // (common after LinkedIn import, where the headline is "Title at Company").
      if (!isRoleLineRedundant(profile.headline, roleLine)) parts.push(roleLine);
    }
  }

  if (profile.location) {
    parts.push(formatLocation(profile.location));
  }

  if (parts.length === 0) {
    return `${profile.displayName ?? profile.handle} on Sifa`;
  }

  return parts.join(' · ');
}
